// SortIndicator — sortable column-header per brand skill §9.3.
// Renders a <th> wrapping a <button>; the button click cycles
// ascending → descending → cleared (null/null), and the th's
// aria-sort attribute reflects the current state.
//
// Active column shows a brand-coloured caret; inactive columns
// render a faint glyph at 25% opacity that brightens on hover.
// The aria-live announcement is a tab-level concern — this
// component just emits onSortChange and the caller wires the
// live region.

import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

export type SortDirection = "ascending" | "descending" | null;
export type SortColumn = string;

export interface SortChangePayload {
  column: SortColumn | null;
  direction: SortDirection;
}

export interface SortIndicatorProps {
  column: SortColumn;
  label: string;
  activeColumn: SortColumn | null;
  direction: SortDirection;
  onSortChange: (next: SortChangePayload) => void;
  /** Optional className applied to the <th> wrapper. */
  thClassName?: string;
}

function nextDirection(
  isActive: boolean,
  direction: SortDirection,
): SortChangePayload {
  if (!isActive) return { column: "__placeholder__", direction: "ascending" };
  if (direction === "ascending") {
    return { column: "__placeholder__", direction: "descending" };
  }
  // descending or null → clear
  return { column: null, direction: null };
}

export default function SortIndicator({
  column,
  label,
  activeColumn,
  direction,
  onSortChange,
  thClassName,
}: SortIndicatorProps) {
  const isActive = activeColumn === column;
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? direction === "ascending"
      ? "ascending"
      : direction === "descending"
        ? "descending"
        : "none"
    : "none";

  const handleClick = () => {
    const next = nextDirection(isActive, direction);
    onSortChange({
      column: next.column === "__placeholder__" ? column : null,
      direction: next.direction,
    });
  };

  return (
    <th aria-sort={ariaSort} className={thClassName}>
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Sort by ${label}`}
        className="group inline-flex items-center gap-1 text-caption uppercase tracking-wide text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <span>{label}</span>
        <span
          data-testid={`sort-caret-${column}`}
          className={
            isActive
              ? "text-brand-600 dark:text-brand-400"
              : "opacity-25 group-hover:opacity-60"
          }
          aria-hidden="true"
        >
          {isActive && direction === "ascending" ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : isActive && direction === "descending" ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5" />
          )}
        </span>
      </button>
    </th>
  );
}
